import { CoinIcon, TriangleIcon } from "@solomon-labs/design/components/icons";
import { Text } from "@solomon-labs/design/components/ui";
import { useInView, useReducedMotion } from "motion/react";
import React from "react";

// Blockaid's official art from the synced design system (logo/blockaid in the
// asset manifest), imported as the other partner logos on this page are.
import blockaidLogo from "../../../../../../../.claude/design-system/assets/logos/partners/blockaid.svg";

import {
    LABEL_BOTTOM,
    LABEL_ROOM,
    STRIP_VIEWBOX_HEIGHT,
    STRIP_VIEWBOX_WIDTH
} from "./monitoringStrip";

// The strip's geometry, in viewBox units, in the box it shares with the STRIDE
// strip (see monitoringStrip). Everything hangs off TRACK_CENTER, the dotted
// line the events travel along.
const VIEWBOX_WIDTH = STRIP_VIEWBOX_WIDTH;
const TRACK_CENTER = 128;

// Each event is a Cut element: the Callout's bars with its two opposite corner
// openings, over an empty background. At 32 units the bar reads as a 1px border
// and the 20-unit glyph as the size-5 (1.25rem) icon. Both glyphs render at the
// design system's own weight, so neither stroke nor transform is overridden here.
const EVENT_SIZE = 32;
const CUT_BAR = 1;
const GLYPH_SIZE = 20;
const GLYPH_INSET = (EVENT_SIZE - GLYPH_SIZE) / 2;
// Centers the event on the lane between the two rails.
const EVENT_TOP = TRACK_CENTER - EVENT_SIZE / 2;

// The rails clear the event box by 0.5rem above and below it.
const RAIL_CLEARANCE = 8;
const RAIL_OFFSET = EVENT_SIZE / 2 + RAIL_CLEARANCE;
const RAIL_TOP = TRACK_CENTER - RAIL_OFFSET;
const RAIL_BOTTOM = TRACK_CENTER + RAIL_OFFSET;

// The scan region overhangs the track by 0.5rem at each end, so it reads as
// reaching past what it watches. Its width is the stretch of track under watch,
// held on the strip's center so narrowing it takes the same off either side.
const SCAN_OVERHANG = 8;
const SCAN_TOP = RAIL_TOP - SCAN_OVERHANG;
const SCAN_HEIGHT = RAIL_OFFSET * 2 + SCAN_OVERHANG * 2;
const SCAN_BOTTOM = SCAN_TOP + SCAN_HEIGHT;
const SCAN_CENTER_X = VIEWBOX_WIDTH / 2;
// 96 units of track under watch.
const SCAN_WIDTH = 96;
const SCAN_X = SCAN_CENTER_X - SCAN_WIDTH / 2;
const SCAN_RIGHT = SCAN_X + SCAN_WIDTH;

// The sweep is a hairline, and a stroke straddles its own path, so its travel is
// inset by half a stroke at each end. The painted line then stops flush inside the
// region's edges instead of hanging half a stroke outside them, which with the
// full-height path makes it flush on all four sides at both ends of the travel.
const SWEEP_STROKE = 1;
const SWEEP_FROM = SCAN_X + SWEEP_STROKE / 2;
const SWEEP_TO = SCAN_RIGHT - SWEEP_STROKE / 2;

// The scanner is the status light alone: it spans the region it reports on and
// sits flush on top of it.
const BAR_HEIGHT = 4;
const BAR_WIDTH = SCAN_WIDTH;
const BAR_X = SCAN_X;
const BAR_TOP = SCAN_TOP - BAR_HEIGHT;

// The box starts at the status light, which puts the top rail 12 units down and
// the bottom rail at 60, where the STRIDE strip has its own. The artwork ends at
// the foot of the scan region, 68 units down; the box runs one unit past it to
// the height the two strips share. The callout closes the column as a row below
// it rather than as units reserved inside it, because it is HTML at a fixed px
// height in a box whose units scale with width, so a reserve would mean guessing
// the narrowest layout and wasting the difference everywhere else.
const VIEWBOX_MIN_Y = BAR_TOP;
const VIEWBOX_HEIGHT = STRIP_VIEWBOX_HEIGHT;

// These timings describe an illustrative monitoring cycle, never live telemetry.
// A long ambient cycle is intentional: the brief calls for one event every 8 to 12s.
const CYCLE_MS = 10000;

// The stream is eight events a cycle, the seven coins and the anomaly, 82 units
// apart (an event and a 50-unit gap), so it moves 656 units a cycle: one event
// crosses the scan every 1.25s.
const EVENT_COUNT = 8;
const EVENT_SPACING = 82;
const STREAM_LENGTH = EVENT_COUNT * EVENT_SPACING;

// An event enters from 48 units off the strip's left end and leaves 48 past its
// right end, whole events clear of the edge either way.
const TRAVEL_MARGIN = 48;
const TRAVEL_FROM = -TRAVEL_MARGIN;
const TRAVEL_TO = VIEWBOX_WIDTH + TRAVEL_MARGIN;
const TRAVEL_DISTANCE = TRAVEL_TO - TRAVEL_FROM;

// The strip is shorter than the stream, so each event crosses it in the middle
// of the cycle at the stream's pace and waits off the strip for the rest, half
// before it enters and half after it leaves. The spacing and pace are the
// stream's whatever the strip's width, and the anomaly passes the scan's centre
// at 312/656 of the cycle (47.6%), where the alert timing below expects it.
const TRAVEL_START =
    ((STREAM_LENGTH - TRAVEL_DISTANCE) / 2 / STREAM_LENGTH) * 100;
const TRAVEL_END = 100 - TRAVEL_START;

// Reduced motion freezes the stream as it passes the scan: a coin inside the
// region and one either side at the stream's spacing, which is as many as fit
// whole on the strip. The other coins wait off its left end.
const REST_SLOTS = [-1, 0, 1];
const restX = (index: number): number =>
    index < REST_SLOTS.length
        ? SCAN_CENTER_X - EVENT_SIZE / 2 + REST_SLOTS[index] * EVENT_SPACING
        : TRAVEL_FROM;

// Where the callout sits inside that cycle, in percent of it. The anomaly is
// wholly inside the scan region from 42.7% to 52.4% of the cycle, so from well
// before ALERT_START until after ALERT_HOLD, and it has left the region by
// ALERT_RELEASE.
const ALERT_START = 47;
const ALERT_HOLD = 50;
const ALERT_RELEASE = 64;
const ALERT_CLEAR = 72;

// The design system answers an error on a light bar with a 600ms burst of three
// beats and then holds on: these are the stops of its solomon-lightbar-error-burst,
// which its own intent doc calls a deliberate vocabulary. Laying them over the
// cycle's percentage timeline keeps a beat 150ms on screen whatever the cycle is.
const BURST_MS = 600;
const BURST_STOPS = [0, 0.125, 0.25, 0.375, 0.5, 0.625, 0.75, 1];
const BURST_FRAMES = BURST_STOPS.slice(1)
    .map((stop, index) => {
        const at = ALERT_START + stop * ((BURST_MS / CYCLE_MS) * 100);
        // The stops alternate off and on from the dark start, so they end lit.
        return `${at.toFixed(3)}% { opacity: ${(index + 1) % 2}; }`;
    })
    .join("\n        ");

const MONITORING_CSS = `
    .onchain-monitoring {
        --monitoring-cycle: ${CYCLE_MS}ms;
        --monitoring-sweep: 4s;
        --monitoring-heartbeat: 2s;
    }
    .onchain-monitoring-event {
        animation: onchain-event-travel var(--monitoring-cycle) linear infinite;
    }
    .onchain-monitoring-scan {
        animation: onchain-monitoring-scan var(--monitoring-sweep) linear infinite;
    }
    .onchain-monitoring-status {
        animation: onchain-monitoring-heartbeat var(--monitoring-heartbeat) var(--ease-out-cubic) infinite;
    }
    .onchain-monitoring-alert,
    .onchain-monitoring-alarm {
        opacity: 0;
    }
    .onchain-monitoring-alert {
        animation: onchain-monitoring-alert var(--monitoring-cycle) var(--ease-out-cubic) infinite;
    }
    .onchain-monitoring-alarm {
        /* Same duration and easing as the alert, so the two share the release
           segment exactly and land back on the green heartbeat together. */
        animation: onchain-monitoring-alarm var(--monitoring-cycle) var(--ease-out-cubic) infinite;
    }
    .onchain-monitoring[data-motion="paused"] [data-monitoring-motion] {
        animation-play-state: paused;
    }
    @keyframes onchain-event-travel {
        0%, ${TRAVEL_START.toFixed(3)}% { transform: translateX(${TRAVEL_FROM}px); }
        ${TRAVEL_END.toFixed(3)}%, 100% { transform: translateX(${TRAVEL_TO}px); }
    }
    @keyframes onchain-monitoring-scan {
        0% { transform: translateX(${SWEEP_FROM}px); opacity: 0; }
        12%, 78% { opacity: 0.55; }
        100% { transform: translateX(${SWEEP_TO}px); opacity: 0; }
    }
    @keyframes onchain-monitoring-heartbeat {
        0%, 30% { opacity: 1; }
        55%, 70% { opacity: 0.2; }
        100% { opacity: 1; }
    }
    @keyframes onchain-monitoring-alert {
        0%, ${ALERT_START}% { opacity: 0; }
        ${ALERT_HOLD}%, ${ALERT_RELEASE}% { opacity: 1; }
        ${ALERT_CLEAR}%, 100% { opacity: 0; }
    }
    @keyframes onchain-monitoring-alarm {
        0%, ${ALERT_START}% { opacity: 0; }
        ${BURST_FRAMES}
        ${ALERT_RELEASE}% { opacity: 1; }
        ${ALERT_CLEAR}%, 100% { opacity: 0; }
    }
    @media (prefers-reduced-motion: reduce) {
        .onchain-monitoring [data-monitoring-motion] { animation: none; }
        .onchain-monitoring-event { transform: translateX(var(--event-rest)); }
        .onchain-monitoring-scan { transform: translateX(${SCAN_CENTER_X}px); opacity: 0.4; }
        /* The alert and alarm layers are already opacity 0 with their animations
           off. Only the anomaly needs hiding. */
        .onchain-monitoring-anomaly { display: none; }
    }
`;

interface EventStyle extends React.CSSProperties {
    "--event-rest": string;
}

/** The Cut frame from the Callout, drawn in SVG so it rides the event's motion. */
function CutFrame(): React.ReactElement {
    const size = EVENT_SIZE;
    const bar = CUT_BAR;
    return (
        <g className="text-line-tint-neutral">
            {/* top: starts a bar-width in from the top-left opening */}
            <rect
                x={bar}
                y={0}
                width={size - bar}
                height={bar}
                fill="currentColor"
            />
            {/* left: starts a bar-width down from the top-left opening */}
            <rect
                x={0}
                y={bar}
                width={bar}
                height={size - bar}
                fill="currentColor"
            />
            {/* bottom: stops a bar-width short of the bottom-right opening */}
            <rect
                x={0}
                y={size - bar}
                width={size - bar}
                height={bar}
                fill="currentColor"
            />
            {/* right: stops a bar-width short of the bottom-right opening */}
            <rect
                x={size - bar}
                y={0}
                width={bar}
                height={size - bar}
                fill="currentColor"
            />
        </g>
    );
}

/** Conceptual onchain activity, not a live security feed or approval gate. */
export function OnchainMonitoring(): React.ReactElement {
    const figureRef = React.useRef<HTMLElement>(null);
    const inView = useInView(figureRef, { amount: 0.25 });
    const reducedMotion = useReducedMotion();
    const motionPaused = !inView || Boolean(reducedMotion);

    return (
        <section
            className="onchain-monitoring flex flex-col gap-2"
            aria-labelledby="onchain-monitoring-title"
            aria-describedby="onchain-monitoring-description"
            data-motion={motionPaused ? "paused" : "running"}
        >
            <style dangerouslySetInnerHTML={{ __html: MONITORING_CSS }} />
            {/* Heading, the strip at the column's full width, then the
                description: the Security tab sets this block beside the STRIDE
                one, so the strip scales to its half of the row. Stacked below
                lg the figure stops at 30.5rem, the column's width at lg's
                widest, so the strip keeps the scale it has side by side
                rather than growing with a full-width column. */}
            {/* h3: the Security tab's "Monitoring and Response" h2
                sits above this block. */}
            <h3
                id="onchain-monitoring-title"
                className="flex flex-col items-start gap-2"
            >
                <img
                    src={blockaidLogo}
                    alt="Blockaid"
                    className="h-5 w-auto md:h-7"
                />
                <Text variant="small" render={<span />}>
                    Transaction Review &amp; Onchain Monitoring
                </Text>
            </h3>
            <figure
                ref={figureRef}
                className="relative flex w-full max-w-122 min-w-0 flex-col"
                aria-label="Illustration of onchain activity passing through continuous monitoring, with a status light that flags an unusual event when one is found"
            >
                <div
                    className="overflow-hidden"
                    style={{ marginTop: LABEL_ROOM }}
                >
                    <svg
                        viewBox={`0 ${VIEWBOX_MIN_Y} ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
                        className="block w-full"
                        fill="none"
                        aria-hidden="true"
                    >
                        <path
                            d={`M0 ${RAIL_TOP}H${VIEWBOX_WIDTH}M0 ${RAIL_BOTTOM}H${VIEWBOX_WIDTH}`}
                            className="stroke-line-base"
                        />
                        <path
                            d={`M0 ${TRACK_CENTER}H${VIEWBOX_WIDTH}`}
                            className="stroke-line-base"
                            strokeDasharray="2 6"
                        />
                        <defs>
                            {/* The docs scanner's stops (0.5 to 0.12); error is its twin on the error token. */}
                            <linearGradient
                                id="onchain-monitoring-scan-clear"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="0"
                                    stopColor="rgb(var(--color-success-base))"
                                    stopOpacity="0.5"
                                />
                                <stop
                                    offset="1"
                                    stopColor="rgb(var(--color-success-base))"
                                    stopOpacity="0.12"
                                />
                            </linearGradient>
                            <linearGradient
                                id="onchain-monitoring-scan-error"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="0"
                                    stopColor="rgb(var(--color-error-base))"
                                    stopOpacity="0.5"
                                />
                                <stop
                                    offset="1"
                                    stopColor="rgb(var(--color-error-base))"
                                    stopOpacity="0.12"
                                />
                            </linearGradient>
                        </defs>
                        {/* The monitoring layer observes a continuous stream; events never stop here. */}
                        <rect
                            x={SCAN_X}
                            y={SCAN_TOP}
                            width={SCAN_WIDTH}
                            height={SCAN_HEIGHT}
                            fill="url(#onchain-monitoring-scan-clear)"
                        />
                        {/* The scan turns error red while the anomaly is read. */}
                        <rect
                            x={SCAN_X}
                            y={SCAN_TOP}
                            width={SCAN_WIDTH}
                            height={SCAN_HEIGHT}
                            fill="url(#onchain-monitoring-scan-error)"
                            className="onchain-monitoring-alert"
                            data-monitoring-motion
                        />
                        {/* The status light: an idle heartbeat, then the design
                        system's three-beat error burst. It holds on for the rest
                        of the callout and releases on the same stops as the
                        gradient, so the two return to clear together. */}
                        <rect
                            x={BAR_X}
                            y={BAR_TOP}
                            width={BAR_WIDTH}
                            height={BAR_HEIGHT}
                            className="onchain-monitoring-status text-success-base"
                            fill="currentColor"
                            data-monitoring-motion
                        />
                        <rect
                            x={BAR_X}
                            y={BAR_TOP}
                            width={BAR_WIDTH}
                            height={BAR_HEIGHT}
                            className="onchain-monitoring-alarm text-error-base"
                            fill="currentColor"
                            data-monitoring-motion
                        />
                        <g
                            className="onchain-monitoring-scan"
                            data-monitoring-motion
                        >
                            <path
                                d={`M0 ${SCAN_TOP}V${SCAN_BOTTOM}`}
                                className="stroke-tint-base"
                            />
                        </g>
                        {Array.from({ length: EVENT_COUNT - 1 }, (_, index) => (
                            <g
                                key={index}
                                className="onchain-monitoring-event"
                                data-monitoring-motion
                                style={
                                    {
                                        animationDelay: `${(-(index + 1) * CYCLE_MS) / EVENT_COUNT}ms`,
                                        "--event-rest": `${restX(index)}px`
                                    } as EventStyle
                                }
                            >
                                <g transform={`translate(0 ${EVENT_TOP})`}>
                                    <CutFrame />
                                    <g
                                        transform={`translate(${GLYPH_INSET} ${GLYPH_INSET})`}
                                        className="text-line-tint-neutral"
                                    >
                                        <CoinIcon
                                            width={GLYPH_SIZE}
                                            height={GLYPH_SIZE}
                                        />
                                    </g>
                                </g>
                            </g>
                        ))}
                        <g
                            className="onchain-monitoring-event onchain-monitoring-anomaly text-accent-base"
                            data-monitoring-motion
                            style={
                                {
                                    "--event-rest": `${SCAN_CENTER_X - EVENT_SIZE / 2}px`
                                } as EventStyle
                            }
                        >
                            <g transform={`translate(0 ${EVENT_TOP})`}>
                                <CutFrame />
                                <g
                                    transform={`translate(${GLYPH_INSET} ${GLYPH_INSET})`}
                                    className="text-line-tint-neutral"
                                >
                                    <TriangleIcon
                                        width={GLYPH_SIZE}
                                        height={GLYPH_SIZE}
                                    />
                                </g>
                            </g>
                        </g>
                    </svg>
                </div>
                {/* A standing "Illustration" label at the strip's right end, so
                    it is never read as live telemetry. It stands in the room
                    LABEL_ROOM keeps above the strip and ends 0.25rem (gap-1)
                    above the top rail. It floats rather than taking a row, and
                    clears the status light, which is held on the strip's
                    centre. */}
                <Text
                    variant="fine-print"
                    className="absolute right-0 text-foreground-neutral"
                    style={{ bottom: LABEL_BOTTOM }}
                >
                    Illustration
                </Text>
            </figure>
            <Text
                id="onchain-monitoring-description"
                variant="small"
                render={<p />}
                className="text-foreground-muted"
            >
                Privileged transactions can be simulated and checked against
                policy before a co-signature is added. Onchain monitoring
                watches for unusual minting, reserve movement, and authority
                changes. Alerts give responders a path to contain an affected
                account, counterparty, asset, or settlement route.
            </Text>
        </section>
    );
}
